import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getBroadStorefrontLocation,
  getProfilePath,
  getStorefrontReadiness,
  getStorefrontUrl,
} from "../../utils/storefront";
import SupportLink from "../Common/SupportLink";
import FarmAvatar from "../Common/FarmAvatar";
import "./MyFarm.css";

const FARM_RECORD_COLLECTIONS = [
  ["animals", "farmAnimalRecords"],
  ["crops", "farmCropRecords"],
  ["tasks", "farmTasks"],
  ["inventory", "farmInventory"],
  ["equipment", "farmEquipment"],
  ["money", "farmMoneyLogs"],
  ["documents", "farmDocuments"],
  ["contacts", "farmContacts"],
  ["weather", "weatherNotes"],
];

const getName = (profile, user) => {
  const safeProfile = profile || {};
  const safeUser = user || {};
  const emailPrefix = typeof safeUser.email === "string"
    ? safeUser.email.split("@")[0]
    : "";

  return (
    safeProfile.farmName ||
    safeProfile.businessName ||
    safeProfile.storeName ||
    safeProfile.displayName ||
    safeProfile.name ||
    safeUser.displayName ||
    emailPrefix ||
    "Your Farm"
  );
};

const isActiveListing = (item = {}) =>
  !item.archived &&
  !item.deleted &&
  !item.hidden &&
  item.isArchived !== true &&
  item.status !== "archived" &&
  item.status !== "deleted";

const isDueTask = (task = {}) => task.status !== "completed";

const MyFarm = ({ currentUser, userProfile }) => {
  const [records, setRecords] = useState({});
  const [products, setProducts] = useState([]);
  const [farmStands, setFarmStands] = useState([]);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsubs = FARM_RECORD_COLLECTIONS.map(([key, collectionName]) =>
      onSnapshot(
        query(collection(db, collectionName), where("ownerId", "==", currentUser.uid)),
        (snapshot) => {
          setRecords((current) => ({
            ...current,
            [key]: snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
          }));
        },
        () => {
          setRecords((current) => ({ ...current, [key]: [] }));
        }
      )
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsubs = ["ownerId", "userId", "sellerId"].map((field) =>
      onSnapshot(
        query(collection(db, "products"), where(field, "==", currentUser.uid)),
        (snapshot) => {
          setProducts((current) => {
            const next = [
              ...current.filter((item) => item[field] !== currentUser.uid),
              ...snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            ];
            return Array.from(new Map(next.map((item) => [item.id, item])).values());
          });
        },
        () => {}
      )
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const standUnsub = onSnapshot(
      query(collection(db, "farmStands"), where("ownerId", "==", currentUser.uid)),
      (snapshot) => setFarmStands(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      () => setFarmStands([])
    );

    const salesUnsub = onSnapshot(
      query(collection(db, "farmStandSales"), where("ownerId", "==", currentUser.uid)),
      (snapshot) => setSales(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      () => setSales([])
    );

    return () => {
      standUnsub();
      salesUnsub();
    };
  }, [currentUser?.uid]);

  const activeListings = useMemo(() => products.filter(isActiveListing), [products]);
  const safeProfile = userProfile || {};
  const farmDisplayName = getName(safeProfile, currentUser);
  const profilePath = currentUser?.uid
    ? getProfilePath({ id: currentUser.uid, uid: currentUser.uid, ...(userProfile || {}) })
    : "/edit-profile";
  const readiness = getStorefrontReadiness({ profile: safeProfile, products: activeListings });
  const publicUrl = getStorefrontUrl(safeProfile);
  const activeStand = farmStands.find((stand) => stand.isArchived !== true && stand.archived !== true) || null;
  const totalSales = sales.reduce(
    (total, sale) => total + Number(sale.quantity || 0) * Number(sale.price || 0),
    0
  );

  const overviewStats = [
    ["Active listings", activeListings.length],
    ["Animals", records.animals?.length || 0],
    ["Crop records", records.crops?.length || 0],
    ["Tasks due", (records.tasks || []).filter(isDueTask).length],
    ["Inventory items", records.inventory?.length || 0],
    ["Sales logged", `$${totalSales.toFixed(2)}`],
  ];

  const sections = [
    {
      title: "Livestock",
      description: "Animals, health notes, breeding plans, and care history.",
      links: [
        ["View Animals", "/farm-records"],
        ["Add Animal", "/farm-records"],
        ["Breeding Calendar", "/farm-records", "Coming next"],
        ["Birth Records", "/farm-records", "Coming next"],
        ["Vaccination Records", "/farm-records"],
        ["Weight / Health Notes", "/farm-records"],
      ],
    },
    {
      title: "Crops & Garden",
      description: "Crop records, planting, harvest, soil, and weather notes.",
      links: [
        ["View Crop Records", "/farm-records"],
        ["Add Crop", "/farm-records"],
        ["Planting Log", "/farm-records"],
        ["Harvest Log", "/farm-records"],
        ["Soil Notes", "/farm-records"],
        ["Weather Notes", "/farm-records"],
      ],
    },
    {
      title: "Farm Stand / Pickup Spot",
      description: "Fresh finds, porch pickup, cooler pickup, public page, QR, inventory, and sales.",
      links: [
        ["View Public Page", publicUrl || profilePath],
        ["Update Fresh Today", "/home#stand-control"],
        ["Manage Inventory", profilePath],
        ["Log Sale", "/home#today-sales"],
        ["Print QR Code", "/my-farm#print"],
        ["Storefront settings", "/edit-profile"],
      ],
    },
    {
      title: "Marketplace",
      description: "Listings, Plants & Garden, want-ads, and local selling.",
      links: [
        ["Active Listings", profilePath],
        ["Add Listing", "/add-product"],
        ["Plants & Garden", "/plants-garden"],
        ["Looking For / Want-Ad", "/home"],
        ["Trade / Barter", "/home", "Planned"],
      ],
    },
    {
      title: "Jobs & Services",
      description: "Hiring, applications, services, and work reviews.",
      links: [
        ["Post Job", "/map"],
        ["Browse Jobs", "/jobs"],
        ["My Jobs", "/jobs", "Owner view", { dashboardView: "owner" }],
        ["Services Offered", "/browse-users", "Planned"],
        ["Work Reviews", profilePath],
        ["Applications", "/jobs", "Applications", { dashboardView: "owner" }],
      ],
    },
    {
      title: "Equipment",
      description: "Equipment list, maintenance, service records, and fuel notes.",
      links: [
        ["View Equipment", "/farm-records"],
        ["Add Equipment", "/farm-records"],
        ["Maintenance Notes", "/farm-records"],
        ["Fuel Log", "/farm-records", "Coming next"],
      ],
    },
    {
      title: "Inventory",
      description: "Feed, medicine, supplies, low stock, and farm materials.",
      links: [
        ["View Inventory", "/farm-records"],
        ["Add Inventory Item", "/farm-records"],
        ["Low Stock", "/farm-records"],
        ["Feed / Medicine / Supplies", "/farm-records"],
      ],
    },
    {
      title: "Money & Reports",
      description: "Sales, expenses, exports, and report prep.",
      links: [
        ["Money Logs", "/farm-records"],
        ["Sales Logs", "/home#today-sales"],
        ["Expenses", "/farm-records"],
        ["Print Reports", "/farm-records"],
        ["Export Records", "/farm-records"],
        ["Tax Categories", "/farm-records", "Placeholder"],
      ],
    },
    {
      title: "Documents",
      description: "Receipts, vet records, soil tests, permits, and uploads.",
      links: [
        ["View Documents", "/farm-records"],
        ["Upload Document", "/farm-records"],
        ["Receipts", "/farm-records"],
        ["Vet Records", "/farm-records"],
        ["Soil Tests", "/farm-records"],
        ["Permits", "/farm-records"],
      ],
    },
    {
      title: "Contacts",
      description: "Vets, buyers, sellers, workers, and local helpers.",
      links: [
        ["View Contacts", "/farm-records"],
        ["Add Contact", "/farm-records"],
        ["Vets", "/farm-records"],
        ["Buyers", "/farm-records"],
        ["Sellers", "/farm-records"],
        ["Workers", "/farm-records"],
      ],
    },
    {
      title: "Tasks & Reminders",
      description: "Today's tasks, upcoming reminders, and Billy suggestions.",
      links: [
        ["Today's Tasks", "/farm-records"],
        ["Add Task", "/farm-records"],
        ["Upcoming Reminders", "/farm-records"],
        ["Billy Suggestions", "/home"],
      ],
    },
  ];

  return (
    <div className="my-farm-page page-shell page-shell-wide">
      <header className="my-farm-hero">
        <div>
          <p className="eyebrow">Operations hub</p>
          <h1>My Farm</h1>
          <p>Home is daily action. My Farm is your full farm binder and operations center.</p>
        </div>
        <div className="my-farm-hero-actions">
          <Link to="/home" className="btn-primary">Back to Home</Link>
          <Link to="/home#stand-control" className="btn-soft">Update Today's Fresh Finds</Link>
          <Link to={publicUrl || profilePath} className="btn-soft">View Public Page</Link>
          <SupportLink className="btn-soft" />
        </div>
      </header>

      <section className="my-farm-overview">
        <div className="my-farm-profile-card">
          <FarmAvatar
            profile={{ ...safeProfile, name: farmDisplayName }}
            className="my-farm-avatar"
          />
          <div>
            <h2>{farmDisplayName}</h2>
            <p>{getBroadStorefrontLocation(safeProfile) || "Broad location not added yet"}</p>
            <div className="my-farm-badges">
              {(safeProfile.role || safeProfile.type) && <span>{safeProfile.role || safeProfile.type}</span>}
              <span>{readiness.ready ? "Profile Complete" : "Profile Needs Setup"}</span>
              {activeStand && <span>Stand / Pickup Added</span>}
            </div>
          </div>
        </div>

        <div className="my-farm-stat-grid">
          {overviewStats.map(([label, value]) => (
            <article key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </article>
          ))}
        </div>
      </section>

      {!readiness.ready || activeListings.length === 0 || !activeStand ? (
        <section className="my-farm-quick-start">
          <div>
            <p className="eyebrow">Quick start</p>
            <h2>{userProfile ? "Build your farm hub in a few steps." : "Welcome. Let's set up your farm hub."}</h2>
          </div>
          {[ 
            ["Complete your profile", readiness.ready],
            ["Add your first animal/crop/listing", activeListings.length > 0 || (records.animals?.length || 0) > 0 || (records.crops?.length || 0) > 0],
            ["Add a stand / pickup spot", Boolean(activeStand)],
            ["Add a task/reminder", (records.tasks?.length || 0) > 0],
            ["Share your public page", Boolean(publicUrl)],
          ].map(([label, done]) => (
            <span key={label} className={done ? "done" : ""}>{done ? "Done" : "Next"} {label}</span>
          ))}
          <p className="dashboard-helper support-helper">
            Need help setting this up? <SupportLink>Contact support</SupportLink>
          </p>
        </section>
      ) : null}

      <section className="my-farm-section-grid">
        {sections.map((section) => (
          <article key={section.title} className="my-farm-section-card">
            <div>
              <h2>{section.title}</h2>
              <p>{section.description}</p>
            </div>
            <div className="my-farm-link-grid">
              {section.links.map(([label, to, badge, state]) => (
                <Link key={`${section.title}-${label}`} to={to} state={state}>
                  {label}
                  {badge && <span>{badge}</span>}
                </Link>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
};

export default MyFarm;
